/**
 * Recipe Action Bar Component
 *
 * Sprint 6: Context-aware action buttons based on recipe status.
 * - Draft: Edit, Publish, Delete
 * - Published: Create Draft, Calculate Cost, Archive
 */

import { Edit, Send, Trash2, Calculator, FilePlus, Save, X } from 'lucide-react';
import { Button } from '@/components/ui';
import type { RecipeWithRelations } from '@bstock/shared';
import { useFormatDate } from '@/hooks';

interface RecipeActionBarProps {
  /** Recipe to show actions for */
  recipe: RecipeWithRelations;
  /** Whether we're in edit mode */
  isEditing: boolean;
  /** Edit mode has unsaved changes */
  isDirty: boolean;
  /** Loading states */
  isLoading?: {
    publish?: boolean;
    delete?: boolean;
    calculate?: boolean;
    createDraft?: boolean;
    save?: boolean;
  };
  /** Whether to show financial actions */
  showFinancials?: boolean;
  /** Whether user can edit */
  canEdit?: boolean;
  /** Whether user can delete */
  canDelete?: boolean;
  /** Callbacks */
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onPublish: () => void;
  onDelete: () => void;
  onCalculateCost: () => void;
  onCreateDraft: () => void;
}

export function RecipeActionBar({
  recipe,
  isEditing,
  isDirty,
  isLoading = {},
  showFinancials = false,
  canEdit = true,
  canDelete = true,
  onEdit,
  onCancelEdit,
  onSave,
  onPublish,
  onDelete,
  onCalculateCost,
  onCreateDraft,
}: RecipeActionBarProps) {
  const { formatDateShort } = useFormatDate();
  const isDraft = recipe.isDraft;
  const isArchived = recipe.validTo !== null;

  // EDIT MODE actions
  if (isEditing) {
    return (
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancelEdit}>
          <X className="w-4 h-4" />
          Annuler
        </Button>
        <div className="flex items-center gap-2">
          {isDraft && (
            <Button
              variant="secondary"
              onClick={onPublish}
              disabled={isLoading.publish || !isDirty}
              isLoading={isLoading.publish}
            >
              <Send className="w-4 h-4" />
              Publier
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!isDirty || isLoading.save}
            isLoading={isLoading.save}
          >
            <Save className="w-4 h-4" />
            Enregistrer
          </Button>
        </div>
      </div>
    );
  }

  // READ MODE - Draft
  if (isDraft) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isLoading.delete}
              isLoading={isLoading.delete}
              className="text-danger-500 hover:text-danger-600 hover:bg-danger-50"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={onPublish}
                disabled={isLoading.publish}
                isLoading={isLoading.publish}
              >
                <Send className="w-4 h-4" />
                Publier
              </Button>
              <Button variant="primary" size="sm" onClick={onEdit}>
                <Edit className="w-4 h-4" />
                Modifier
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // READ MODE - Archived
  if (isArchived) {
    return (
      <div className="flex items-center justify-end">
        <div className="text-sm text-neutral-400">
          Archivee le {formatDateShort(recipe.validTo!)}
        </div>
      </div>
    );
  }

  // READ MODE - Published (active)
  return (
    <div className="flex items-center justify-end">
      <div className="flex items-center gap-2">
        {showFinancials && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCalculateCost}
            disabled={isLoading.calculate}
            isLoading={isLoading.calculate}
          >
            <Calculator className="w-4 h-4" />
            Recalculer cout
          </Button>
        )}
        {canEdit && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onCreateDraft}
              disabled={isLoading.createDraft}
              isLoading={isLoading.createDraft}
            >
              <FilePlus className="w-4 h-4" />
              Creer brouillon
            </Button>
            <Button variant="primary" size="sm" onClick={onEdit}>
              <Edit className="w-4 h-4" />
              Modifier
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
